// packages/core/src/schedule/geometry.ts
//
// Calcule les cinq valeurs CSS de la grille V2 (D33e) et les positions des
// plages. Aucun accès DOM ici : `pointerCoarse` est un booléen fourni par
// l'appelant (via `matchMedia('(pointer: coarse)')` côté composant), pour
// que ce module reste testable sans jsdom.

import type { Step } from "./types";

/** Hauteur de ligne (px) par pas, à la souris (D33f). */
export const ROW_HEIGHT_MOUSE: Record<Step, number> = {
  15: 24,
  30: 32,
  60: 48,
  120: 64,
  240: 80,
};

/** Hauteur de ligne minimale au tactile, quel que soit le pas (D33f). */
export const MIN_TOUCH_ROW_HEIGHT = 44;

/** Largeur de la colonne des heures et largeur mini d'une colonne de jour (D33g). */
export const HOUR_COL_WIDTH = 56;
export const DAY_MIN_WIDTH = 96;

export interface GridDimensions {
  /** `--row-h` : hauteur d'une ligne = un pas, en px. */
  rowHeight: number;
  /** `--px-per-min` : pixels par minute, dérivé de rowHeight / step. */
  pxPerMin: number;
  hourColWidth: number;
  dayMinWidth: number;
}

/**
 * Les cinq variables CSS de la grille (D33e). C'est la SEULE fonction que le
 * composant appelle pour connaître les dimensions ; tout le reste (position
 * des plages, lignes de fond) est calculé en CSS à partir de ces valeurs.
 */
export function computeGridDimensions(
  step: Step,
  pointerCoarse: boolean,
): GridDimensions {
  const mouseHeight = ROW_HEIGHT_MOUSE[step];
  const rowHeight = pointerCoarse
    ? Math.max(MIN_TOUCH_ROW_HEIGHT, mouseHeight)
    : mouseHeight;
  return {
    rowHeight,
    pxPerMin: rowHeight / step,
    hourColWidth: HOUR_COL_WIDTH,
    dayMinWidth: DAY_MIN_WIDTH,
  };
}

/**
 * Position verticale (px) d'une minute donnée par rapport au haut de la
 * grille défilable (sous l'en-tête), pour une fenêtre visible qui commence à
 * `visibleStartMinute`. Utilisée à la fois pour le haut d'une plage et pour
 * le trait de la ligne d'heure — ce sont, par construction (D33n), LA MÊME
 * formule appliquée à des minutes différentes.
 */
export function topForMinute(
  minute: number,
  visibleStartMinute: number,
  pxPerMin: number,
): number {
  return (minute - visibleStartMinute) * pxPerMin;
}

export function heightForDuration(
  durationMinutes: number,
  pxPerMin: number,
): number {
  return durationMinutes * pxPerMin;
}

/**
 * Nombre de lignes de la grille entre `startMinute` et `endMinute` (bornes de
 * la fenêtre visible), au pas donné.
 */
export function rowCount(
  startMinute: number,
  endMinute: number,
  step: Step,
): number {
  return Math.ceil((endMinute - startMinute) / step);
}

/**
 * Recentre le défilement vertical après un changement de pas (D33o) :
 * renvoie le nouveau `scrollTop` pour que la minute qui était au centre de
 * la zone visible avant le changement reste au centre après.
 *
 * `viewportHeight` est la hauteur de la zone des LIGNES, en-tête collant
 * exclu : l'en-tête des jours masque le haut du conteneur, le centre utile
 * est celui de ce qui reste en dessous. Le résultat n'est borné qu'à 0 — le
 * navigateur borne lui-même au maximum défilable.
 */
export function scrollTopToKeepCentered(
  centerMinuteBefore: number,
  visibleStartMinute: number,
  pxPerMinAfter: number,
  viewportHeight: number,
): number {
  const centerOffsetAfter = topForMinute(
    centerMinuteBefore,
    visibleStartMinute,
    pxPerMinAfter,
  );
  return Math.max(0, centerOffsetAfter - viewportHeight / 2);
}

/**
 * Minute correspondant au centre de la zone visible, pour ancrer le
 * défilement (D33o). Inverse exacte de `scrollTopToKeepCentered` (hors
 * bornage) ; même convention pour `viewportHeight`.
 */
export function centerMinuteFromScroll(
  scrollTop: number,
  visibleStartMinute: number,
  pxPerMin: number,
  viewportHeight: number,
): number {
  return visibleStartMinute + (scrollTop + viewportHeight / 2) / pxPerMin;
}
